import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { QuestionnaireType } from 'src/API';
import { compareDateStrings } from 'src/app/core/api/date.util';
import { PatientService } from 'src/app/core/api/patient.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { ChipColour } from 'src/app/shared/chip/chip.component';
import { QuestionnaireStatusPipe } from 'src/app/shared/shared-pipes/questionnaire-status.pipe';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService
} from './../../../core/api/questionnaire-request.service';
import { QuestionnaireResponseModalComponent } from './../../../shared/consult-forms/questionnaire-response-modal/questionnaire-response-modal.component';
import { QrCodeModalComponent } from './../../../shared/qr-code-modal/qr-code-modal.component';
import { QuestionnaireColumnTab, SendRow, SendRowsByTab } from './questionnaire-column.model';
import { RecordDetailHost } from '../record-detail-panel/record-detail-host';
import { SideSheetService } from '../../../shared/side-sheet/side-sheet.service';

@Component({
  selector: 'csi-questionnaires-table',
  templateUrl: './questionnaires-table.component.html',
  styleUrls: ['./questionnaires-table.component.scss'],
  providers: [QuestionnaireStatusPipe]
})
export class QuestionnairesTableComponent extends RecordDetailHost<string>
  implements OnInit, OnDestroy, DoCheck {
  public readonly translocoFileKey = 'dashboard.patients-dashboard.';

  // Same reason as the assessments column: the rebuild replaced a live [dataSource] binding with a
  // snapshot, so a questionnaire sent from this column's own header would not appear until the
  // patient was reloaded.
  private lastSeenPatientId: string = null;
  private lastSeenQuestionnaires: QuestionnaireRequest[] = null;
  public readonly QuestionnaireType = QuestionnaireType;

  public readonly columnTabs: QuestionnaireColumnTab[] = ['all', 'completed', 'awaiting', 'expired'];
  public readonly tabLabelMap: { [tab in QuestionnaireColumnTab]: string } = {
    all: 'All',
    completed: 'Completed',
    awaiting: 'Awaiting',
    expired: 'Expired'
  };

  private readonly statusChipColourMap: { [status: string]: ChipColour } = {
    Completed: 'green-no-outline',
    Incomplete: 'orange',
    Expired: 'red-no-outline'
  };

  private readonly statusIconClassMap: { [status: string]: string } = {
    Completed: 'send-icon-done',
    Incomplete: 'send-icon-awaiting',
    Expired: 'send-icon-expired'
  };

  public rowsByTab: SendRowsByTab = { all: [], completed: [], awaiting: [], expired: [] };
  public visibleSendRows: SendRow[] = [];
  public activeTab: QuestionnaireColumnTab = 'all';
  public isSortedNewestFirst = true;

  constructor(
    public router: Router,
    private dialog: MatDialog,
    public questionnaireRequestService: QuestionnaireRequestService,
    public patientService: PatientService,
    private questionnaireStatusPipe: QuestionnaireStatusPipe,
    private translocoService: TranslocoService,
    private appStateService: AppStateService,
    sideSheetService: SideSheetService
  ) {
    super(sideSheetService);
  }

  ngOnInit() {
    this.appStateService.currentPatient$.pipe(untilDestroyed(this)).subscribe(patient => {
      const patientId = patient ? patient.id : null;
      if (patientId !== this.lastSeenPatientId) {
        this.lastSeenPatientId = patientId;
        this.closeDetail();
      }
      this.refreshRows();
    });
  }

  ngOnDestroy() {
    this.closeDetail();
  }

  ngDoCheck() {
    if (this.patientService.questionnaires !== this.lastSeenQuestionnaires) {
      this.refreshRows();
    }
  }

  public refreshRows() {
    const questionnaires = this.patientService.questionnaires || [];
    this.lastSeenQuestionnaires = this.patientService.questionnaires;
    const newestFirst = questionnaires
      .slice()
      .sort((first, second) => this.compareCreatedAtDescending(first, second));

    const allSendRows = this.buildSendRows(newestFirst);
    this.rowsByTab = {
      all: allSendRows,
      completed: allSendRows.filter(sendRow => sendRow.status === 'Completed'),
      awaiting: allSendRows.filter(sendRow => sendRow.status === 'Incomplete'),
      expired: allSendRows.filter(sendRow => sendRow.status === 'Expired')
    };
    this.applyTab();
  }

  private applyTab() {
    const rowsForTab = this.rowsByTab[this.activeTab];
    this.visibleSendRows = this.isSortedNewestFirst ? rowsForTab : rowsForTab.slice().reverse();
  }

  public selectTab(tab: QuestionnaireColumnTab) {
    this.activeTab = tab;
    this.applyTab();
  }

  public toggleSortOrder() {
    this.isSortedNewestFirst = !this.isSortedNewestFirst;
    this.applyTab();
  }

  public selectQuestionnaire(sendRow: SendRow) {
    if (!sendRow.isCompleted || !this.patientService.patient) {
      return;
    }

    this.toggleDetail(
      sendRow.questionnaire.id,
      () =>
        QuestionnaireResponseModalComponent.buildDialogData(
          sendRow.questionnaire,
          null,
          this.patientService.patient.id
        ),
      QuestionnaireResponseModalComponent
    );
  }

  public openQRCode(questionnaire: QuestionnaireRequest) {
    const path = `${location.origin}/questionnaires/${questionnaire.id}`;
    const questionnaireName = this.questionnaireRequestService.getQuestionnaireFriendlyName(
      questionnaire
    );

    const scanTitle = this.translocoService.translate('Scan the QR Code to open the');
    const questionnaireLabel = this.translocoService.translate('questionnaire');

    this.dialog.open(QrCodeModalComponent, {
      data: {
        qrCodePath: path,
        modalTitle: scanTitle + ' ' + questionnaireName + ' ' + questionnaireLabel
      }
    });
  }

  public trackBySendRow(index: number, sendRow: SendRow): string {
    return sendRow.questionnaire.id;
  }

  public trackByTab(index: number, tab: QuestionnaireColumnTab): string {
    return tab;
  }

  private toSentOnDate(questionnaire: QuestionnaireRequest): string {
    const sentOn = questionnaire.createdAt;
    const completedOn = questionnaire.completedAt;
    if (sentOn && completedOn && compareDateStrings(sentOn, completedOn) > 0) {
      return completedOn;
    }
    return sentOn;
  }

  private buildSendRows(newestFirst: QuestionnaireRequest[]): SendRow[] {
    return newestFirst.map(questionnaire => {
      const status = this.questionnaireStatusPipe.transform(questionnaire);
      const linkedAssessment = (questionnaire as any).assessment;

      return {
        name: this.questionnaireRequestService.getQuestionnaireFriendlyName(questionnaire),
        status: status,
        statusChipColour: this.statusChipColourMap[status] || 'red-no-outline',
        statusIconClass: this.statusIconClassMap[status] || 'send-icon-expired',
        isCompleted: status === 'Completed',
        isIncomplete: status === 'Incomplete',
        sentOn: this.toSentOnDate(questionnaire),
        completedOn: questionnaire.completedAt || '',
        linkedAssessmentDate:
          linkedAssessment && linkedAssessment.createdAt ? linkedAssessment.createdAt : '',
        questionnaire: questionnaire
      };
    });
  }

  private compareCreatedAtDescending(
    first: QuestionnaireRequest,
    second: QuestionnaireRequest
  ): number {
    return new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime();
  }
}
